const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

type RGB = { r: number; g: number; b: number };

type HSL = { h: number; s: number; l: number };

function parseHexColor(color: string): RGB | undefined {
  const hex3Match = color.match(/^#([0-9a-fA-F]{3})$/);
  if (hex3Match) {
    const [r, g, b] = hex3Match[1].split('');
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
    };
  }

  const hex6Match = color.match(/^#([0-9a-fA-F]{6})$/);
  if (hex6Match) {
    return {
      r: parseInt(hex6Match[1].slice(0, 2), 16),
      g: parseInt(hex6Match[1].slice(2, 4), 16),
      b: parseInt(hex6Match[1].slice(4, 6), 16),
    };
  }

  return undefined;
}

function parseRgbColor(color: string): RGB | undefined {
  const rgbMatch = color.match(
    /^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*((?:\d*\.\d+)|\d+))?\)$/i
  );

  if (!rgbMatch) {
    return undefined;
  }

  return {
    r: clamp(Number(rgbMatch[1]), 0, 255),
    g: clamp(Number(rgbMatch[2]), 0, 255),
    b: clamp(Number(rgbMatch[3]), 0, 255),
  };
}

function parseCssColor(color: string): RGB | undefined {
  const normalized = color.trim();
  const fromHex = parseHexColor(normalized);
  if (fromHex) return fromHex;

  const fromRgb = parseRgbColor(normalized);
  if (fromRgb) return fromRgb;

  // Supports CSS named colors in browser environments.
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    ctx.fillStyle = '#000000';
    ctx.fillStyle = normalized;
    const resolved = ctx.fillStyle;
    return parseHexColor(resolved) ?? parseRgbColor(resolved);
  }

  return undefined;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / delta + 2;
        break;
      default:
        h = (rNorm - gNorm) / delta + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: h * 360,
    s: s * 100,
    l: l * 100,
  };
}

function averageHsl(colors: string[]): HSL {
  const parsed = colors
    .map((color) => parseCssColor(color))
    .filter((rgb): rgb is RGB => rgb !== undefined)
    .map(rgbToHsl);

  if (parsed.length === 0) {
    return { h: 0, s: 0, l: 0 };
  }

  let sumSin = 0;
  let sumCos = 0;
  let sumS = 0;
  let sumL = 0;

  parsed.forEach(({ h, s, l }) => {
    const hueRad = (h * Math.PI) / 180;
    sumSin += Math.sin(hueRad);
    sumCos += Math.cos(hueRad);
    sumS += s;
    sumL += l;
  });

  const count = parsed.length;
  let avgHue = (Math.atan2(sumSin / count, sumCos / count) * 180) / Math.PI;
  if (avgHue < 0) avgHue += 360;

  return {
    h: avgHue,
    s: sumS / count,
    l: sumL / count,
  };
}

export function averageColorsInHSL(colors: string[]): string {
  const { h, s, l } = averageHsl(colors);
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

export function averageColorsInHSLA(colors: string[], alpha = 0.35): string {
  const { h, s, l } = averageHsl(colors);
  return `hsla(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%, ${clamp(
    alpha,
    0,
    1
  ).toFixed(2)})`;
}
